import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_05 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import { 
  MeasuringUnitMessages,
  UITextMessages,
  getInputMaxLengthMessage
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MeasuringUnitItem {
  immeasuringunitcode?: string | number;
  immeasuringunit?: string;
  descrip?: string;
}

interface MeasuringUnitDetails {
  immeasuringunit: string;
  descrip: string;
  immeasuringunitcode?: string | number;
}

interface ErrorState {
  immeasuringunit?: string;
  descrip?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  RESTCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: MeasuringUnitItem;
  actionText?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreateMeasuringUnits(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const [data, setData] = useState<MeasuringUnitItem[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const location = useLocation();
  const { selectedItem } = (location.state as LocationState) || {};
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, RESTCODE, USERNAME, COMPCODE } =
    SYS_USER.USER_PARAM || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  const [selectedItemDetails, setSelectedItemDetails] = useState<MeasuringUnitDetails>({
    immeasuringunit: "",
    descrip: "",
  });

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        immeasuringunit: selectedItem.immeasuringunit || "",
        descrip: selectedItem.descrip || "",
        immeasuringunitcode: selectedItem.immeasuringunitcode,
      });
    }
  }, [selectedItem]);

  const handleCancel = (): void => {
    // Clear any error messages and navigate back to the previous page
    setError({});
    navigate("/vCreateMeasuringUnits");
  };

  useEffect(() => {
    setPageData({
      title: UITextMessages.breadcrumb.measuringUnit,
    } as PageData);
  }, [setPageData]);

  const goBackToView = (): void => {
    navigate("/vCreateMeasuringUnits");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate max length
    if (typeof value === "string" && value.length > 30) {
      setError((prevError) => ({
        ...prevError,
        [name]: MeasuringUnitMessages.error.inputMaxLength,
      }));
      return;
    }

    // Validate empty string
    if (typeof value === "string" && trimmedValue === "") {
      setError((prevError) => ({
        ...prevError,
        [name]: MeasuringUnitMessages.error.inputEmpty,
      }));
    } else {
      setError((prevError) => ({
        ...prevError,
        [name]: "",
      }));
    }

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleInputChangedes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate empty string
    if (typeof value === "string" && trimmedValue === "") {
      setError((prevError) => ({
        ...prevError,
        [name]: MeasuringUnitMessages.error.inputEmpty,
      }));
    } else {
      setError((prevError) => ({
        ...prevError,
        [name]: "",
      }));
    }

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await externalgetApi(SETTING_API_05, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });

        setData(result as MeasuringUnitItem[]);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setAlertType("error");
        setAlertMessage(MeasuringUnitMessages.error.fetchFailed);
        setAlertOpen(true);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);
    const { immeasuringunit } = selectedItemDetails;

    const requiredFields = [
      {
        name: "immeasuringunit",
        value: immeasuringunit,
        label: UITextMessages.label.measuringUnit,
      },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) => !field.value
    );
    
    if (missingRequiredFields.length > 0) {
      const newError: ErrorState = {};
      missingRequiredFields.forEach((field) => {
        newError[field.name] = MeasuringUnitMessages.warning.nameRequired;
      });
      setError(newError);

      setAlertType("error");
      setAlertMessage(MeasuringUnitMessages.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    if (data) {
      const isDuplicate = data.some((item) => {
        if (
          item.immeasuringunitcode !== selectedItemDetails.immeasuringunitcode &&
          item.immeasuringunit?.toLowerCase() === immeasuringunit?.toLowerCase()
        ) {
          return true;
        } else {
          return false;
        }
      });

      if (isDuplicate) {
        setAlertMessage(MeasuringUnitMessages.warning.alreadyExists);
        setAlertType("warning");
        setAlertOpen(true);
        setIsButtonDisabled(false);
        return;
      }
    }

    const BRAND_CODE = selectedItem ? selectedItem.immeasuringunitcode : 0;

    const staticValues = {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    const payload = {
      ...selectedItemDetails,
      ...staticValues,
      immeasuringunitcode: BRAND_CODE,
    };

    try {
      const responseData = await externalpostApi(SETTING_API_05, payload);
      console.log("Success:", responseData);
      setAlertMessage(
        isEdit ? MeasuringUnitMessages.success.update : MeasuringUnitMessages.success.create
      );
      setAlertType("success");
      setAlertOpen(true);
      setSelectedItemDetails({
        immeasuringunit: "",
        descrip: "",
      });
      
      setTimeout(() => {
        navigate("/vCreateMeasuringUnits");
      }, 2000);
    } catch (error) {
      const err = error as ApiError;
      console.error("Error:", err);
      setAlertType("error");
      setAlertMessage(err.response?.data?.message || MeasuringUnitMessages.error.unexpected);
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title={UITextMessages.tooltip.back} arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            {UITextMessages.breadcrumb.measuringUnit}
          </Link>
        </Breadcrumbs>
      </Box>
      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10, md: 10, lg: 10, xl: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? UITextMessages.pageTitle.edit : UITextMessages.pageTitle.create}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={styles.add_form_page_container}
        >
          <Grid container spacing={2} sx={styles.add_form_page_grid_container}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_measuringunit"
                label={UITextMessages.label.measuringUnit}
                variant="outlined"
                fullWidth
                size="small"
                required
                name="immeasuringunit"
                value={selectedItemDetails.immeasuringunit}
                onChange={handleInputChange}
                error={!!error.immeasuringunit}
                helperText={error.immeasuringunit}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
                inputProps={{
                  maxLength: 30,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <TextField
                id="txt_descrip"
                label={UITextMessages.label.description}
                variant="outlined"
                fullWidth
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChangedes}
                error={!!error.descrip}
                helperText={error.descrip}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_cancel"
            variant="outlined"
            size="medium"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            {UITextMessages.button.cancel || "Cancel"}
          </Button>
          <Button
            id="btn_confirm"
            variant="contained"
            size="medium"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            disabled={isButtonDisabled}
            onClick={handleConfirmClick}
          >
            {isEdit ? UITextMessages.button.modify : UITextMessages.button.confirm}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateMeasuringUnits;